/**
 * The platform super admin working inside someone else's account: one line says whose, and takes him back to his own.
 * Going back changes his role version on purpose, so the version the routine returns is stored before the session is
 * read again (R6) — he is never signed out by it.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { storeRoleVersion, useSession } from '@/lib/session';
import { notSaved, writeRpc } from '@/lib/write';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';

export function PlatformBanner() {
  const { session, refresh } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  if (session.state !== 'authenticated-single-tenant' || !session.isPlatformAdmin || !session.platformHome) return null;
  if (session.orgId === session.platformHome.orgId) return null;
  const back = async () => {
    setBusy(true);
    const r = await writeRpc<number>(supabase, 'platform_back_to_home', {});
    if (!r.ok || typeof r.data !== 'number') { setBusy(false); toast.error(r.ok ? notSaved('/rest/v1/rpc/platform_back_to_home') : r.message, 'write-not-saved'); return; }
    storeRoleVersion(session.userId, r.data);
    await refresh();
    void qc.resetQueries();
    setBusy(false);
    nav('/platform');
  };
  return (
    <div role="status" data-testid="platform-banner" className="z-banner flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-brand-tint px-4 py-1.5 text-center text-sm text-ink ring-1 ring-brand/40">
      <span className="min-w-0 max-w-full truncate">Working in <strong className="font-semibold">{session.orgName}</strong></span>
      <Button size="dense" variant="secondary" busy={busy} busyLabel="Going back…" onClick={() => void back()}>Back to my account</Button>
    </div>
  );
}
